import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  AlertTitle,
  MenuItem,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useRecordPaymentMutation } from '../../Service/extendedInvoicesApi';
import type { RecordPaymentRequest } from '../../Types/paymentTypes';
import { invoiceLogger } from '../../utils/invoiceLogger';
import {
  getCashDocumentsErrorCode,
  getCashDocumentsErrorMessage,
} from '../../../../Utils/cashDocumentsErrorMapping';
import { applyApiValidationErrorsToForm } from '../../utils/invoiceApiErrorHandling';
import { CashDocumentsRoutes } from '../../../../Router/path';

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  remainingAmount: number;
  onSuccess?: (isCashPayment: boolean) => void;
}

interface PaymentFormData {
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  note?: string; // Optional field
}

const PAYMENT_METHODS = ['Gotówka', 'Karta płatnicza', 'PayPal', 'Stripe', 'Inne'];

// Validation schema
const validationSchema: yup.ObjectSchema<PaymentFormData> = yup.object({
  amount: yup
    .number()
    .required('Kwota jest wymagana')
    .positive('Kwota musi być dodatnia')
    .test('max-decimals', 'Maksymalnie 2 miejsca po przecinku', (value) => {
      if (!value) return true;
      return /^\d+(\.\d{1,2})?$/.test(value.toString());
    }),
  paymentDate: yup
    .string()
    .required('Data płatności jest wymagana')
    .test('not-future', 'Data płatności nie może być w przyszłości', (value) => {
      if (!value) return true;
      const selectedDate = dayjs(value);
      const tomorrow = dayjs().add(1, 'day').startOf('day');
      return selectedDate.isBefore(tomorrow);
    }),
  paymentMethod: yup.string().required('Metoda płatności jest wymagana').max(50),
  note: yup.string().max(500, 'Notatka nie może przekraczać 500 znaków').optional(),
});

/**
 * Dialog for recording a payment for an invoice.
 * Uses React Hook Form for form management and Yup for validation.
 */
const RecordPaymentDialog: React.FC<RecordPaymentDialogProps> = ({
  open,
  onClose,
  invoiceId,
  remainingAmount,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [recordPayment, { isLoading, error }] = useRecordPaymentMutation();
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | null>(null);
  const [showCashReportGuardCta, setShowCashReportGuardCta] = React.useState(false);
  const submitFallbackMessage = 'Błąd podczas rejestracji płatności. Sprawdź dane i spróbuj ponownie.';
  const cashReportGuardErrorCode = 'CASH_DOCUMENT_MISSING_OPEN_REPORT_CYCLE';

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<PaymentFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      amount: remainingAmount > 0 ? remainingAmount : 0,
      paymentDate: dayjs().format('YYYY-MM-DD'),
      paymentMethod: 'Gotówka',
      note: '',
    },
  });

  // Watch payment method to show auto-KP info for cash payments
  const watchPaymentMethod = useWatch({
    control,
    name: 'paymentMethod',
  });

  // Detect cash payment (case-insensitive)
  const isCashPayment = watchPaymentMethod?.toLowerCase().includes('gotówka') || false;

  const onSubmit = async (data: PaymentFormData) => {
    clearErrors();
    setSubmitErrorMessage(null);
    setShowCashReportGuardCta(false);

    try {
      const request: RecordPaymentRequest = {
        amount: data.amount,
        paymentDate: data.paymentDate,
        paymentMethod: data.paymentMethod,
        note: data.note || undefined,
      };

      await recordPayment({ invoiceId, data: request }).unwrap();

      // Success - notify parent and close dialog
      onSuccess?.(isCashPayment);
      reset();
      onClose();
    } catch (err) {
      const errorCode = getCashDocumentsErrorCode(err);
      setShowCashReportGuardCta(errorCode === cashReportGuardErrorCode);
      applyApiValidationErrorsToForm(err, setError);
      setSubmitErrorMessage(getCashDocumentsErrorMessage(err, submitFallbackMessage));
      invoiceLogger.error('Failed to record payment:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      clearErrors();
      setSubmitErrorMessage(null);
      setShowCashReportGuardCta(false);
      reset();
      onClose();
    }
  };

  const handleOpenCashReports = () => {
    handleClose();
    navigate(CashDocumentsRoutes.root);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rejestracja płatności</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* API Error */}
          {(submitErrorMessage || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitErrorMessage ||
                getCashDocumentsErrorMessage(error, submitFallbackMessage)}
              {showCashReportGuardCta && (
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: 1, display: 'inline-flex' }}
                  onClick={handleOpenCashReports}
                >
                  Przejdź do raportów kasowych
                </Button>
              )}
            </Alert>
          )}

          {/* Remaining Amount Info */}
          {remainingAmount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Pozostało do zapłaty: <strong>{remainingAmount.toFixed(2)} PLN</strong>
            </Alert>
          )}

          {/* Auto-KP Info for Cash Payments */}
          {isCashPayment && (
            <Alert severity="info" icon={<ReceiptIcon />} sx={{ mb: 2 }}>
              <AlertTitle>Automatyczne wystawienie dokumentu KP</AlertTitle>
              Dla płatności gotówkowych system automatycznie wystawi dokument KP (kasa przyjęła) w
              celu zachowania prawidłowej ewidencji kasowej. Będziesz mógł edytować dane dokumentu w
              sekcji "Dokumenty kasowe (KP/KW)" poniżej.
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Amount */}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Kwota *"
                    type="number"
                    fullWidth
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    inputProps={{
                      step: '0.01',
                      min: '0.01',
                    }}
                  />
                )}
              />
            </Grid>

            {/* Payment Date */}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data płatności *"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.paymentDate}
                    helperText={errors.paymentDate?.message}
                  />
                )}
              />
            </Grid>

            {/* Payment Method */}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Metoda płatności *"
                    fullWidth
                    error={!!errors.paymentMethod}
                    helperText={errors.paymentMethod?.message}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Note */}
            <Grid size={{ xs: 12 }}>
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notatka (opcjonalnie)"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.note}
                    helperText={errors.note?.message || `${(field.value || '').length}/500 znaków`}
                    placeholder="Dodatkowe informacje o płatności..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Anuluj
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Zapisywanie...' : 'Zapisz płatność'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordPaymentDialog;
